<template>
  <div>
    <div class="grid-container">
      <div class="main-section results-count-bar" :class="{
        'list-mode': currentView === 'list',
        'grid-mode': currentView === 'grid',
      }">
        <div class="result-sec">
          <p class="result-text">
            <strong>{{ totalResults }}</strong> Results Found
          </p>
          <p v-if="aiGeoFallback" class="geo-fallback-notice">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            No matches near your location — showing results from other areas.
          </p>
          <!-- Structured search found nothing for the location / service type,
               so the list below was widened. Same deal as the Deals page: say
               what happened instead of showing an empty page. -->
          <p v-else-if="listingFallback" class="geo-fallback-notice">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ listingFallback.message }}
          </p>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="grid-card-wrapper">
      <!-- Empty state (only shown when not loading and truly no results) -->
      <div v-if="!loading && services.length === 0" class="empty-state-panel">
        <div class="empty-state-content">
          <h3 class="empty-title">No Listings Found</h3>
          <p class="empty-description">
            {{ emptyStateMessage }}
          </p>

          <!-- Broaden search suggestion -->
          <div v-if="canBroaden" class="empty-broaden">
            <button class="broaden-btn" @click="broadenSearch">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              Try expanding to 50 miles
            </button>
          </div>

          <!-- Popular categories nearby -->
          <div v-if="suggestions?.popularCategories?.length" class="empty-categories">
            <p class="empty-section-title">Popular near you</p>
            <div class="category-chips">
              <button
                v-for="cat in suggestions.popularCategories"
                :key="cat.name"
                class="category-chip"
                @click="searchByCategory(cat.name)"
              >
                {{ cat.name }} <span class="chip-count">{{ cat.count }}</span>
              </button>
            </div>
          </div>

          <!-- Nearby cities with listings -->
          <div v-if="suggestions?.nearbyCities?.length" class="empty-cities">
            <p class="empty-section-title">Listings in nearby cities</p>
            <div class="city-cards">
              <button
                v-for="city in suggestions.nearbyCities"
                :key="city.city"
                class="city-card"
                @click="searchByCity(city)"
              >
                <span class="city-name">{{ city.city }}</span>
                <span class="city-meta">{{ city.count }} listing{{ city.count > 1 ? 's' : '' }} · {{ city.distanceMiles }} mi</span>
              </button>
            </div>
          </div>

          <!-- Recent nearby listings -->
          <div v-if="suggestions?.recentNearby?.length" class="empty-recent">
            <p class="empty-section-title">Recent listings nearby</p>
            <div class="recent-cards">
              <div
                v-for="item in suggestions.recentNearby"
                :key="item.servicesID"
                class="recent-card"
                @click="navigateToPost(item)"
              >
                <img
                  :src="item.imageUrl || defaultImage"
                  class="recent-card-img"
                  alt="listing"
                  @error="onRecentImgError"
                />
                <div class="recent-card-info">
                  <p class="recent-card-title">{{ item.servicesName }}</p>
                  <p class="recent-card-location">{{ item.location }}</p>
                  <span class="recent-card-badge">{{ item.serviceType1 }}</span>
                  <span v-if="item.distanceMiles" class="recent-card-dist">{{ item.distanceMiles }} mi away</span>
                </div>
              </div>
            </div>
          </div>

          <!-- CTA -->
          <div class="empty-cta">
            <p class="empty-cta-text">{{ ctaText }}</p>
            <button class="empty-btns" @click="postad">Post Free Ad</button>
          </div>
        </div>
      </div>

      <!-- Content always stays in DOM; overlay dims it while loading -->
      <div v-else class="content-wrapper" :class="{ 'is-loading': loading }">
        <!-- Spinner overlay sits on top without removing the list -->
        <div v-if="loading" class="loader-overlay">
          <PulseLoader size="16px" color="var(--color-primary)" />
        </div>
        <keep-alive>
          <component :is="viewMap[currentView]" :services="services" />
        </keep-alive>

        <!-- Thin result set: offer the whole category in one click rather than
             making the user clear the location and sub-type by hand. -->
        <div v-if="showAllScope" class="show-all-bar">
          <button class="show-all-btn" type="button" @click="showAllInScope">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
            {{ showAllScope.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <!-- <Pagination
     v-if="totalPages > 1 && paginationReady"
      :totalPages="totalPages"
      v-model="currentPage"
      @input="handlePageChange"
    /> -->
    <Pagination v-if="totalPages > 1" :totalPages="totalPages" v-model="currentPage" listType="dashboard" :viewMode="currentView" />
        <AuthModal v-if="showModal" :mode="modalMode" @close="showModal = false" @switchMode="switchMode" />
  <PostModal
  v-if="showPostModal"
  :type="postMode"
  :postId="postId"
  @close="showPostModal = false"
/>
  </div>
</template>

<script>
import ListView from "./ListView.vue";
import GridCard from "./GridCard.vue";
// import MapView from "./MapView.vue";
import { getDashboardData, searchServices as searchServicesAPI, aiSearchServices, getEmptyStateSuggestions } from "@/services/dashboardService";
import Pagination from "./pagination.vue";
import { EventBus } from "../../services/EventBus.js";
import PulseLoader from "vue-spinner/src/PulseLoader.vue";
import AuthModal from "./Models/AuthModal.vue";
import PostModal from "./Models//PostModal.vue";
import axios from "axios";
import locationService from "../../services/locationService.js";
import { getIPLocation } from "../../services/ipLocationService.js";
import { clearListScroll } from "@/utils/listScrollMemory";
import { canonicalSlug } from "@/utils/slug";
import { taxonomyParents } from "@/utils/serviceTypeMatcher";
import listScrollRestore from "@/mixins/listScrollRestore";
export default {
  mixins: [listScrollRestore],
  components: {
    GridCard,
    ListView,
    Pagination,
    // MapView,
    PulseLoader,
    AuthModal,
    PostModal
  },
  props: {
    initialServices: {
      type: Array,
      default: () => [],
    },
    initialTotal: {
      type: Number,
      default: 0,
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ["restore-filters", "view-changed", "sort-changed"],
  data() {
    return {
       modalMode: "login",
        postMode: "new",
         showLogin: false,
          showModal: false,
    showPostModal: false,
   isvalid: false,
      totalResults: 0,
      services: [],
      currentView: "grid",
      currentPage: 1,
      totalPages: 1,
      loading: false,
      isMenuOpen: false,
      isRestoring: false,
      // Newest-first is the default browse order everywhere — a listing's
      // freshness matters more than its distance. "nearest" stays available
      // through the sort cycle.
      sortBy: "latest",
      perPage: 12,
      isResetting: false,
      isRestoringFilters: false,
      appliedFilters: {},
      // Sticky AI query: once an AI nav-search lands, keep using aiSearchServices
      // for pagination and back-from-detail restore. Cleared by any real filter
      // change (filters watcher diff). Without this, page 2 falls into the
      // structured branch with stale filters and counts/results diverge.
      activeAISearchQuery: "",
      activeAISearchGeo: null, // sticky {lat,lng,label} from the hero "Where" field for AI pagination/restore
      aiGeoFallback: false, // true when the AI geo search found nothing nearby and results are nationwide
      // Set when a structured search came back empty and was re-run with the
      // location (and, if needed, the service type) dropped: {scope, message}.
      // Null whenever the results on screen are the ones actually asked for.
      listingFallback: null,
      suggestions: null, // empty-state suggestions (nearby cities, categories, recent)
      viewMap: {
        grid: "GridCard",
        list: "ListView",
        // map: "MapView",
      },
    };
  },


  watch: {

    // Re-apply the saved list position the first time results render after a
    // Back from /view-post. Hooking the data (rather than a specific created()
    // branch) covers every path that fills the list — sessionStorage restore,
    // the CitywisePage/landing-page pre-seed, and the plain fresh fetch.
    services(newServices) {
      this.applyListScroll(newServices);
    },

    filters: {
      deep: true,
      handler(newFilters) {
        if (this.isRestoringFilters) return;

        // Only reset page when filters actually changed compared to last applied.
        // Canonicalize to the query-driving fields so an identically-valued but
        // freshly-spread filters object (common — onFilterApplied always creates
        // a new object reference) doesn't look "changed" and knock the user back
        // to page 1 while paginating.
        const areEqual =
          this.canonicalFilterKey(newFilters) ===
          this.canonicalFilterKey(this.appliedFilters);

        if (!areEqual) {
          // The results are about to change — the remembered position no
          // longer points at anything meaningful.
          this.discardListScroll();

          if (this.currentPage !== 1) {
            // Suppress the currentPage watcher's refetch — we invoke fetch below
            // with the new filters. Without this guard, both watchers fire and
            // a stale response can overwrite the fresh one.
            this.isRestoring = true;
            this.currentPage = 1;
            this.$nextTick(() => { this.isRestoring = false; });
          }

          // Any real filter edit breaks out of sticky AI mode — the user is
          // narrowing/changing the query, so structured search takes over.
          this.activeAISearchQuery = "";
          this.activeAISearchGeo = null;

          // persistState runs at the end of fetchDashboardData once
          // appliedFilters reflects the resolved query (incl. coords resolved
          // from typed userinput). Calling it here would save pre-resolution
          // nulls and lose the geo scope on back-from-detail.
          this.fetchDashboardData(newFilters, 1);
        }
      },
    },
    currentPage(newPage) {
      if (this.isRestoring) return;

      // User-driven page change (the restore path is guarded above).
      this.discardListScroll();

      // Paginate with the SAME fully-resolved query that produced the results
      // currently on screen. appliedFilters is set by fetchDashboardData after
      // every fetch and carries the resolved scope — userinput, distance, and
      // the latOverride/lngOverride coords. this.filters is the parent (GridView)
      // prop, which on the CitywisePage pre-seeded path (/:subtype/:city) only
      // ever receives selectedServiceType/selectedSubType and has NO location or
      // coords — so paging off it silently drops the geo scope and broadens the
      // search (e.g. Jersey City 29 results → ~600). Fall back to this.filters
      // for the legacy/first-render case where no fetch has run yet.
      const base =
        this.appliedFilters && Object.keys(this.appliedFilters).length
          ? this.appliedFilters
          : this.filters;

      // A widened query (_resolved) means the empty sub-type is deliberate, so
      // it must be passed through untouched. Topping it up from the EventBus —
      // which still holds the sub-type the user picked from the menu — re-ran
      // the dead scope on page 2, got nothing, widened again and dumped the
      // user back on page 1. Only a query we did NOT resolve ourselves gets the
      // EventBus top-up (the menu path can reach here before the filters prop
      // has caught up).
      const paged = base._resolved
        ? { ...base }
        : {
            ...base,
            selectedServiceType:
              base.selectedServiceType || EventBus.selectedServiceType,
            selectedSubType:
              base.selectedSubType || EventBus.selectedSubtype,
          };

      // fetchDashboardData persists state after the response lands, so the
      // saved currentPage matches the page the user is actually viewing.
      this.fetchDashboardData(paged, newPage);
    },
  },
  mounted() {
  this.verifyToken();
    document.addEventListener("click", this.closeMenu);
    window.addEventListener("beforeunload", this.clearSessionState);

    // Watch for preferred country change and refetch data.
    // Fires for any change, including "Others" (empty string) which means
    // "no country filter". Clears sticky AI mode so the structured branch
    // (which honors country) handles the fetch.
    this.$watch(
      () => EventBus.selectedCountryName,
      (newCountry) => {
        this.discardListScroll();
        this.activeAISearchQuery = "";
        this.activeAISearchGeo = null;
        this.currentPage = 1;
        this.fetchDashboardData({
          ...this.filters,
          preferredCountryName: newCountry || ""
        }, 1);
      }
    );

    // Watch for nav search bar query changes
    this.$watch(
      () => EventBus.searchQuery,
      (newQuery) => {
        if (newQuery) {
          this.discardListScroll();
          this.currentPage = 1;
          this.fetchDashboardData({ searchQuery: newQuery }, 1);
        }
      }
    );
  },
  beforeUnmount() {
    document.removeEventListener("click", this.closeMenu);
    window.removeEventListener("beforeunload", this.clearSessionState);
    // beforeRouteLeave below only fires on the *routed* component; Views is
    // a grandchild of dashboard.vue, so we can't rely on it. beforeUnmount
    // does fire when the user navigates away (e.g. to /view-post/:slug),
    // so persist here and clear comingFromMenu — otherwise on back-from-detail
    // the menu branch in created() overrides sessionStorage and the location
    // / page / filters are dropped.
    this.persistState();
    // Stop any in-flight scroll restore so its timers can't scroll whatever
    // page replaces this one.
    this.discardListScroll();
    EventBus.comingFromMenu = false;
  },
  computed: {
    sortLabel() {
      const labels = { nearest: "Nearest", latest: "Newest", oldest: "Oldest" };
      return labels[this.sortBy] || "Sortby";
    },
    activeFilterCount() {
      let count = 0;
      if (this.filters.userinput || this.filters.distance) count++;
      if (this.filters.selectedServiceType || this.filters.selectedSubType)
        count++;

      if (this.sortBy && this.sortBy !== "latest") count++;
      return count;
    },
    emptyStateMessage() {
      const parts = [];
      const cat = this.filters?.selectedServiceType || this.filters?.selectedSubType;
      const loc = this.filters?.userinput;
      if (cat) parts.push(`for "${cat}"`);
      if (loc) parts.push(`near ${loc}`);
      if (parts.length) return `No listings found ${parts.join(" ")}. But don't worry — here are some alternatives:`;
      return "No listings match your search. But don't worry — here are some alternatives:";
    },
    canBroaden() {
      const dist = this.filters?.distance;
      return dist && parseInt(dist) < 50;
    },
    // The query that produced what is on screen. appliedFilters is the widened
    // one after a fallback, so prefer it — the filters prop still describes the
    // dead search the user typed.
    resultsScope() {
      return this.appliedFilters && Object.keys(this.appliedFilters).length
        ? this.appliedFilters
        : (this.filters || {});
    },
    // "Show all …" button under a thin result set. A handful of listings is
    // usually a scope problem, not an inventory problem, so offer the category
    // in one click instead of making the user clear filters by hand. Returns
    // null when the button should not be there at all.
    showAllScope() {
      // An empty result set widens itself (widenEmptyResults) and a full page
      // has pagination to offer instead — this sits between the two.
      if (this.loading) return null;
      if (!this.services.length || this.totalResults >= this.perPage) return null;

      const base = this.resultsScope;
      const subType = base.selectedSubType || "";
      const serviceInfo = base.selectedServiceInfo || "";
      const hasLocation = !!(
        base.userinput || base.latOverride || base.lngOverride || base.stateCode || base.distance
      );
      // Already as wide as this button can go.
      if (!hasLocation && !subType && !serviceInfo) return null;

      const category =
        base.selectedServiceType ||
        taxonomyParents(subType).category ||
        taxonomyParents(serviceInfo).category ||
        "";
      return {
        category,
        label: category ? `Show all ${category} listings` : "Show all listings",
      };
    },
    ctaText() {
      const cat = this.filters?.selectedServiceType || this.filters?.selectedSubType || "";
      const loc = this.filters?.userinput || "";
      if (cat && loc) return `Be the first to post ${cat} in ${loc}!`;
      if (cat) return `Be the first to post ${cat} in your area!`;
      if (loc) return `Be the first to post a listing in ${loc}!`;
      return "Be the first to post a listing in your area!";
    },
    defaultImage() {
      // "No image" placeholder: light card with a picture/mountains icon
      return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64' viewBox='0 0 64 64'%3E%3Crect width='64' height='64' rx='8' fill='%23eef2ff'/%3E%3Crect x='15' y='19' width='34' height='26' rx='4' fill='none' stroke='%23a9b4d0' stroke-width='2.5'/%3E%3Ccircle cx='25' cy='29' r='3.5' fill='%23a9b4d0'/%3E%3Cpath d='M19 45 L29 33 L36 40 L42 35 L49 45 Z' fill='%23a9b4d0'/%3E%3C/svg%3E";
    },
  },

   

  async created() {
    // The back-from-detail scroll position (saved by GridCard/ListView when the
    // user opened the listing) is taken by the listScrollRestore mixin's
    // created(), which runs before this one, and applied by the `services`
    // watcher as soon as the list has rendered.

    // ✅ MENUs FIRST LOAD
    if (EventBus.comingFromMenu) {
      // A menu click is a fresh browse, not a return — start at the top.
      this.discardListScroll();

      const filters = {
        selectedServiceType: EventBus.selectedServiceType,
        selectedSubType: EventBus.selectedSubtype || "",
        withPhotosOnly: this.filters.withPhotosOnly === true,
      };

      this.isRestoring = true;

      this.currentPage = 1;
      await this.fetchDashboardData(filters, 1);
      this.isRestoring = false;

      EventBus.comingFromMenu = false;
      EventBus.userInteracted = false;

      return;
    }

    // ✅ RESTORE DASHBOARD STATE
    const savedState = sessionStorage.getItem("dashboardState");
    if (savedState) {
      const state = JSON.parse(savedState);

      // Only restore state that was saved for the route we're on now. A state
      // saved on the dashboard ("/") must not be restored onto /jobs (or any
      // other landing page) — that would surface listings from an unrelated
      // earlier search. Legacy states saved before routePath existed have none
      // and stay dashboard-scoped (honored exactly as before).
      const currentRoutePath = (this.$route && this.$route.path) || "/";
      const routeMatches = !state.routePath || state.routePath === currentRoutePath;
      if (!routeMatches) {
        sessionStorage.removeItem("dashboardState");
      } else {
        // Sanitize stale sessionStorage: an older version of the code stored the
        // full {lat,lng,label} object as userinput. Extract the string label so
        // the API payload and EventBus always receive a plain string.
        const rawInput = state.filters && state.filters.userinput;
        if (rawInput && typeof rawInput !== 'string') {
          state.filters.userinput =
            typeof rawInput === 'object' && rawInput.label ? rawInput.label : '';
        }

        // Sessions saved before the photos-only toggle existed won't have
        // withPhotosOnly. Default to false so the first restored fetch matches
        // the toggle's default "All Listings" UI state.
        if (state.filters && state.filters.withPhotosOnly === undefined) {
          state.filters.withPhotosOnly = false;
        }

        // Block AdvanceFilter from running its own watcher-triggered search
        // while we restore — the fetch below is the source of truth; a
        // competing search would race it and overwrite the restored results.
        EventBus.restoreInProgress = true;
        this.isRestoring = true;
        this.isRestoringFilters = true;

        // Sessions saved before this field existed have no currentView, and
        // viewMap[undefined] resolves to no component at all — a blank results
        // area. Fall back to the same default as data().
        this.currentView = state.currentView || "grid";
        // Sessions saved before newest-first became the default carry the old
        // "nearest" (or nothing at all) — fall back to data()'s default so a
        // stale session can't pin the user back onto distance sorting.
        this.sortBy = state.sortBy || "latest";
        this.currentPage = state.currentPage;
        // Restore sticky AI query BEFORE fetch so the AI branch fires for
        // back-from-detail (instead of falling into structured search with the
        // mirrored parsedIntent values, which produce slightly different counts).
        this.activeAISearchQuery = state.activeAISearchQuery || "";
        this.activeAISearchGeo = state.activeAISearchGeo || null;
        this.$emit("view-changed", this.currentView);
        this.$emit("sort-changed", this.sortLabel);

        // 🔥 restore filters to parent — without _resolved. GridView merges what
        // it receives into its own filters object and never clears a key, so the
        // flag would stick to every later user-edited search and wrongly suppress
        // the EventBus top-up. It belongs to the fetch below, not to the prop.
        const { _resolved, ...propFilters } = state.filters || {};
        this.$emit("restore-filters", propFilters);

        try {
          await this.fetchDashboardData(
            state.filters,
            this.currentPage,
            this.sortBy
          );
        } finally {
          EventBus.restoreInProgress = false;
        }
        // The restored query is already the widened one (persistState saves
        // appliedFilters), so it returns results and clears the notice on the
        // way in — put it back so the explanation survives back-from-detail.
        if (state.listingFallback && this.services.length && !this.listingFallback) {
          this.listingFallback = state.listingFallback;
        }
        this.isRestoring = false;
        this.$nextTick(() => {
          this.isRestoringFilters = false;
        });
        return;
      }
    }

    // If parent passed initial services (e.g., city-specific page), use them and skip initial fetch
    if (this.initialServices && this.initialServices.length) {
      this.services = [...this.initialServices];
      const totalCount = this.initialTotal || this.services.length;
      this.totalResults = totalCount;
      this.totalPages = Math.ceil(totalCount / this.perPage);
      return;
    }

    // ── Pre-seeded from URL (e.g. CitywisePage /:subtype/:city) ─────────────
    // Only use EventBus values when the current route actually carries city
    // params — this prevents stale EventBus data from a previous CitywisePage
    // visit from overriding IP-based location on the regular dashboard.
    const isCitywiseRoute = this.$route && this.$route.params && this.$route.params.city;
    const preSeededLoc = isCitywiseRoute && EventBus.selectedLocation;
    if (preSeededLoc) {
      const preLocStr = typeof preSeededLoc === 'string'
        ? preSeededLoc
        : (preSeededLoc.label || '');

      // Block the filters watcher from double-fetching when AdvanceFilter's
      // debounced scheduleSearch() fires in the same tick.
      this.isRestoringFilters = true;
      // Extract coords from EventBus when available to skip re-geocoding
      const preLat = (typeof preSeededLoc === 'object') ? preSeededLoc.lat : null;
      const preLng = (typeof preSeededLoc === 'object') ? preSeededLoc.lng : null;
      await this.fetchDashboardData({
        userinput: preLat ? '' : preLocStr,  // skip text geocoding when we have coords
        distance: preLocStr ? 30 : null,
        selectedSubType: EventBus.selectedSubtype || '',
        selectedServiceType: EventBus.selectedServiceType || '',
        latOverride: preLat || null,
        lngOverride: preLng || null,
        withPhotosOnly: this.filters.withPhotosOnly === true,
      });
      this.$nextTick(() => { this.isRestoringFilters = false; });
      return;
    }

    // ── Fresh load: resolve initial location before fetching listings ────────
    // Priority: localStorage cache → user's saved default (API) → IP geolocation
    const { label, lat, lng, region, region_code } = await this._resolveInitialLocation();

    // Seed EventBus so the nearby-cities panel and AdvanceFilter stay in sync.
    // Use an OBJECT when we have real coordinates — dashboard.vue handles {lat,lng}
    // directly and AdvanceFilter ignores non-strings (so no stray geocoding runs).
    // Use a plain STRING for named cities (from saved profile / localStorage cache)
    // so AdvanceFilter can populate the search box and geocode normally.
    if (lat && lng) {
      EventBus.selectedLocation = { lat, lng, label: label || "" };
    } else if (label) {
      EventBus.selectedLocation = label;
    }

    // Derive a 2-letter state code for geo-fallback filtering.
    // region_code may already be "NJ" or may be the full name "New Jersey".
    const stateCode = this._resolveStateCode(region_code, region);

    await this.fetchDashboardData({
      userinput: lat ? "" : (label || ""),  // skip text geocoding when we already have coords
      distance: (lat || label) ? 30 : null,
      latOverride: lat || null,
      lngOverride: lng || null,
      stateCode: stateCode || null,
      withPhotosOnly: this.filters.withPhotosOnly === true,
    });
  },

  methods: {

    // ── Canonical fingerprint of the query-driving filter fields ────────────
    // Used to decide whether a new filters prop object represents a real change
    // vs. a same-value re-spread. Keys are hard-coded and sorted so the output
    // is stable regardless of insertion order on either side of the compare.
    canonicalFilterKey(f) {
      const obj = f || {};
      const norm = (v) => (v === undefined || v === "" ? null : v);
      const latLng = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      };
      return JSON.stringify({
        userinput: norm(obj.userinput),
        distance: norm(obj.distance),
        selectedServiceType: norm(obj.selectedServiceType),
        selectedSubType: norm(obj.selectedSubType),
        selectedServiceInfo: norm(obj.selectedServiceInfo),
        withPhotosOnly: obj.withPhotosOnly === true,
        sortBy: norm(obj.sortBy),
        preferredCountryName: norm(obj.preferredCountryName),
        latOverride: latLng(obj.latOverride),
        lngOverride: latLng(obj.lngOverride),
        stateCode: norm(obj.stateCode),
      });
    },

    // ── Resolve the best available location on first load ────────────────────
    // Returns { label, lat, lng } — all may be null if no location is available.
    // Priority: localStorage cache → user saved default (API) → browser geolocation
    async _resolveInitialLocation() {
      // 1. Fast path: localStorage cache (sync, no network)
      const cached = locationService.getDefaultLocation();
      if (cached?.city) {
        return {
          label: cached.city,
          lat: cached.lat || null,
          lng: cached.lng || null,
        };
      }

      // 2. Logged-in user: fetch saved default location from API
      const userId = localStorage.getItem("userid");
      if (userId) {
        const saved = await locationService.loadDefaultLocation();
        if (saved?.city) {
          return {
            label: saved.city,
            lat: saved.lat || null,
            lng: saved.lng || null,
          };
        }
      }

      // 3. IP-based geolocation — city-level accuracy, no browser permission popup.
      //    Uses the shared singleton so only ONE request is ever made per page load.
      const ipLoc = await getIPLocation();
      if (ipLoc?.lat && ipLoc?.lng) {
        return { label: ipLoc.label, lat: ipLoc.lat, lng: ipLoc.lng, region: ipLoc.region, region_code: ipLoc.region_code };
      }

      return { label: null, lat: null, lng: null, region: null, region_code: null };
    },

    _resolveStateCode(regionCode, region) {
      // If already a 2-letter code, use it directly
      if (regionCode && regionCode.length === 2) return regionCode.toUpperCase();
      // Map full state name → abbreviation
      const name = (regionCode || region || "").trim().toLowerCase();
      if (!name) return null;
      const map = {
        "alabama":"AL","alaska":"AK","arizona":"AZ","arkansas":"AR","california":"CA",
        "colorado":"CO","connecticut":"CT","delaware":"DE","florida":"FL","georgia":"GA",
        "hawaii":"HI","idaho":"ID","illinois":"IL","indiana":"IN","iowa":"IA","kansas":"KS",
        "kentucky":"KY","louisiana":"LA","maine":"ME","maryland":"MD","massachusetts":"MA",
        "michigan":"MI","minnesota":"MN","mississippi":"MS","missouri":"MO","montana":"MT",
        "nebraska":"NE","nevada":"NV","new hampshire":"NH","new jersey":"NJ","new mexico":"NM",
        "new york":"NY","north carolina":"NC","north dakota":"ND","ohio":"OH","oklahoma":"OK",
        "oregon":"OR","pennsylvania":"PA","rhode island":"RI","south carolina":"SC",
        "south dakota":"SD","tennessee":"TN","texas":"TX","utah":"UT","vermont":"VT",
        "virginia":"VA","washington":"WA","west virginia":"WV","wisconsin":"WI","wyoming":"WY",
        "district of columbia":"DC",
      };
      return map[name] || null;
    },

    switchMode(type) {
      this.modalMode = type;
    },
    closeMenu(e) {
      const menu = this.$refs.menu;
      const trigger = this.$refs.menuTrigger;

      if (
        this.isMenuOpen &&
        (!menu || !menu.contains(e.target)) &&
        (!trigger || !trigger.contains(e.target))
      ) {
        this.isMenuOpen = false;
      }
    },

    setView(view) {
      this.currentView = view;
      this.$emit("view-changed", view);
      this.persistState();
    },
    persistState() {
      // Source from appliedFilters — the resolved post-fetch state that
      // actually produced the results on screen (includes coords that
      // autocomplete resolved from a typed userinput, stateCode from the
      // initial-load geolocation, etc). Reading from this.filters instead
      // would persist whatever the UI last set, which may lack coords the
      // user never explicitly entered. Only called after a successful fetch.
      const af = this.appliedFilters || {};
      if (!af || !Object.keys(af).length) return;

      const state = {
        source: "dashboard",
        // Tag the state with the route these results belong to so created()
        // only restores it onto the same route. Use the captured results route
        // (not this.$route) — beforeUnmount runs while the router is already
        // mid-navigation to the detail page.
        routePath: this._resultsRoutePath || (this.$route && this.$route.path) || "/",
        filters: {
          userinput: af.userinput || "",
          distance: af.distance || null,
          selectedServiceType: af.selectedServiceType || "",
          selectedSubType: af.selectedSubType || "",
          selectedServiceInfo: af.selectedServiceInfo || "",
          withPhotosOnly: af.withPhotosOnly === true,
          sortBy: af.sortBy || this.sortBy || null,
          preferredCountryName:
            af.preferredCountryName || EventBus.selectedCountryName || "",
          latOverride: af.latOverride || null,
          lngOverride: af.lngOverride || null,
          stateCode: af.stateCode || null,
          // Carried so back-from-detail rehydrates the widened scope instead of
          // letting the EventBus re-apply the sub-type that was dropped.
          _resolved: !!af._resolved,
        },
        currentPage: this.currentPage,
        currentView: this.currentView,
        sortBy: this.sortBy,
        activeAISearchQuery: this.activeAISearchQuery || "",
        activeAISearchGeo: this.activeAISearchGeo || null,
        // The filters above are the widened ones, so back-from-detail returns
        // the same broadened list — keep the line that explains why.
        listingFallback: this.listingFallback || null,
      };

      sessionStorage.setItem("dashboardState", JSON.stringify(state));
    },

    clearSessionState() {
      // Clear dashboard session state on page reload/refresh
      sessionStorage.removeItem("dashboardState");
      // A reload isn't a Back — the list is rebuilt from scratch, so drop the
      // remembered position with the rest of the state.
      clearListScroll();
    },

    // discardListScroll() — abandon a pending/in-flight restore whenever the
    // user changes what the list shows — comes from the listScrollRestore mixin.


    applySort(order) {
      this.discardListScroll();
      this.sortBy = order;
      this.isMenuOpen = false;
      this.currentPage = 1;
      this.$emit("sort-changed", this.sortLabel);

      // Re-sort the SAME resolved query that produced the current results.
      // Same reasoning as the currentPage watcher: this.filters lacks the
      // resolved location/coords on the CitywisePage pre-seeded path, so
      // sorting off it would broaden the search. Prefer appliedFilters.
      const base =
        this.appliedFilters && Object.keys(this.appliedFilters).length
          ? this.appliedFilters
          : this.filters;

      // fetchDashboardData persists post-response with the new sortBy.
      this.fetchDashboardData(base, 1, order);
    },

    cycleSortOrder() {
      // Default ("latest") first, so the first click off the default lands on
      // Nearest and a full cycle returns to Newest.
      const orders = ["latest", "nearest", "oldest"];
      const idx = orders.indexOf(this.sortBy);
      const next = orders[(idx + 1) % orders.length];
      this.applySort(next);
    },

    resetFilters() {
      this.discardListScroll();
      this.sortBy = "latest";
      this.currentPage = 1;
      this.listingFallback = null;

      sessionStorage.removeItem("dashboardState");

      EventBus.resetFilters = true;
      EventBus.clearSearchQuery();
      // mark as a user action so AdvanceFilter will run a fresh search
      EventBus.userInteracted = true;
    },

    async fetchDashboardData(
      filters = {},
      page = this.currentPage,
      sortBy = this.sortBy
    ) {
      // Capture the route these results belong to. fetchDashboardData always
      // runs while the results page is on screen, so this is the page to tag
      // persisted state with (persistState may later run from beforeUnmount,
      // when this.$route has already advanced to the detail page).
      this._resultsRoutePath = (this.$route && this.$route.path) || "/";
      console.log("Filters used:", filters);
      console.log("Page:", page);
      console.log("SortBy:", sortBy);

      // ── Stale-response guard ───────────────────────────────────────────────
      // Rapid toggles of Photos Only / All Listings (or filter changes) can
      // race: an older request may resolve AFTER a newer one and overwrite
      // services / totalResults with the wrong data — causing non-photo
      // listings to appear under Photos Only and counts to disagree with the
      // active toggle. Use a monotonic seq + AbortController so only the
      // latest fetch applies its result, and earlier in-flight requests
      // (including their image loads) are cancelled.
      this._fetchSeq = (this._fetchSeq || 0) + 1;
      const seq = this._fetchSeq;
      if (this._fetchAbort) {
        try { this._fetchAbort.abort(); } catch { /* ignore */ }
      }
      const controller = (typeof AbortController !== "undefined")
        ? new AbortController()
        : null;
      this._fetchAbort = controller;
      const signal = controller ? controller.signal : null;
      const isLatest = () => seq === this._fetchSeq;

      try {
        this.loading = true;
        // Any new fetch owns the notice — a stale "showing all listings" line
        // must not sit above results that really do match the filters.
        this.listingFallback = null;

        // ── Branch: AI-powered search query from nav search bar ──
        // Sticky: once an AI search has landed, paginate / back-from-detail
        // re-runs the same query via aiSearchServices — so totals and per-page
        // results stay consistent. The structured branch only takes over when
        // the user actively edits a filter (which clears activeAISearchQuery).
        const activeSearchQuery =
          (filters && filters.searchQuery) ||
          EventBus.searchQuery ||
          this.activeAISearchQuery ||
          "";
        if (activeSearchQuery) {
          // Geocoded "Where" from the hero dual-input (if any). Explicit geo
          // overrides whatever location the AI parses out of the query text.
          const activeSearchGeo =
            (filters && filters.searchGeo) ||
            EventBus.searchGeo ||
            this.activeAISearchGeo ||
            null;
          const { services, allservicesCount, parsedIntent } = await aiSearchServices({
            query: activeSearchQuery,
            page: page,
            perPage: this.perPage,
            sortBy: sortBy,
            signal,
            lat: activeSearchGeo ? activeSearchGeo.lat : null,
            lng: activeSearchGeo ? activeSearchGeo.lng : null,
            distance: activeSearchGeo ? 30 : null,
          });
          if (!isLatest()) return;
          this.services = [...(services || [])];
          const totalCount = allservicesCount?.totalCount || this.services.length;
          this.totalResults = totalCount;
          this.totalPages = Math.ceil(totalCount / this.perPage);
          this.aiGeoFallback = !!allservicesCount?.geoFallback;
          // On a hit, drop the spinner now. On a miss, the widening below is
          // still fetching — keep it up (the finally clears it) so the empty
          // state never flashes before the widened results land.
          if (totalCount > 0) this.loading = false;

          // Store parsed intent in EventBus for UI display
          EventBus.parsedIntent = parsedIntent || null;

          // Remember the raw query (and its geo scope) so pagination and
          // back-from-detail re-enter this AI branch instead of falling into
          // structured search with stale GridView.filters. Must be captured
          // BEFORE clearSearchQuery() below, which nulls EventBus.searchGeo.
          this.activeAISearchQuery = activeSearchQuery;
          this.activeAISearchGeo = activeSearchGeo;

          // Mirror parsedIntent into AdvanceFilter state so Location / Service
          // Type / Subtype / Distance reflect what the AI matched. The suppress
          // flag lets the watchers sync the UI without firing a duplicate fetch.
          // With an explicit geo, the Where label is the location of record —
          // the parsed city/state are overridden so AdvanceFilter's parsedIntent
          // watcher can't stomp the Location box with "USA" or a wrong city.
          let effectiveIntent = parsedIntent;
          if (parsedIntent && activeSearchGeo && activeSearchGeo.label) {
            const [labelCity, ...labelRest] = activeSearchGeo.label.split(",");
            effectiveIntent = {
              ...parsedIntent,
              city: (labelCity || "").trim(),
              state: labelRest.join(",").trim(),
            };
          }
          const locLabel = activeSearchGeo
            ? activeSearchGeo.label || ""
            : effectiveIntent
              ? [effectiveIntent.city, effectiveIntent.state].filter(Boolean).join(", ")
              : "";
          if (effectiveIntent) {
            EventBus.suppressNextAutoSearch = true;
            EventBus.comingFromMenu = false;
            if (effectiveIntent.serviceType1) {
              EventBus.selectedServiceType = effectiveIntent.serviceType1;
            }
            if (effectiveIntent.serviceType2) {
              EventBus.selectedSubtype = effectiveIntent.serviceType2;
            }
            if (activeSearchGeo) {
              // Keep the coords so a follow-up AdvanceFilter edit preserves the
              // geo scope through the structured branch.
              EventBus.selectedLocation = {
                lat: activeSearchGeo.lat,
                lng: activeSearchGeo.lng,
                label: activeSearchGeo.label || locLabel,
              };
            } else if (locLabel) {
              EventBus.selectedLocation = locLabel;
            }
            // Default radius for AI-driven searches
            EventBus.selectedDistance = 30;
            // Absorb the raw query — subsequent filter edits run through the
            // structured non-AI path, not another AI round-trip. clearSearchQuery
            // also nulls parsedIntent, so restore it for the breadcrumb display.
            EventBus.clearSearchQuery();
            EventBus.parsedIntent = effectiveIntent;
          }

          // Mirror the AI-derived query into appliedFilters so canonicalFilterKey
          // matches GridView's filters prop (set via restore-filters below) and
          // the filters watcher doesn't bounce us back to page 1 on its next fire.
          this.appliedFilters = {
            userinput: locLabel || "",
            distance: locLabel ? 30 : null,
            selectedServiceType: (effectiveIntent && effectiveIntent.serviceType1) || "",
            selectedSubType: (effectiveIntent && effectiveIntent.serviceType2) || "",
            withPhotosOnly: this.filters?.withPhotosOnly === true,
            sortBy: sortBy,
            preferredCountryName: EventBus.selectedCountryName || "",
            latOverride: activeSearchGeo ? activeSearchGeo.lat : null,
            lngOverride: activeSearchGeo ? activeSearchGeo.lng : null,
            stateCode: null,
          };

          // Persist so back-from-detail re-runs the same AI query at the same page.
          this.persistState();

          // Nothing matched the query anywhere — widen instead of showing an
          // empty page, same as the structured branch below.
          if (totalCount === 0) {
            const widened = await this.widenAISearch({
              query: activeSearchQuery,
              intent: effectiveIntent,
              sortBy,
              signal,
              isLatest,
            });
            if (!isLatest()) return;
            if (widened) return;
            this.fetchEmptyStateSuggestions();
          } else {
            this.suggestions = null;
          }
          return;
        }
        EventBus.parsedIntent = null;
        this.aiGeoFallback = false;

        // Always prefer explicit filters passed in, fallback to EventBus values.
        //
        // _resolved marks a query we widened ourselves (an empty-result fallback
        // or the "Show all …" button), where an empty sub-type means "dropped",
        // not "not set yet". Without this exemption the EventBus still holds the
        // sub-type the user picked from the menu, so paging or restoring a
        // widened result set silently re-applied the very scope that was
        // dropped — page 2 of "all Services listings" came back as page 2 of
        // "Service Wanted" (i.e. empty, then widened again to page 1).
        const resolved = !!(filters && filters._resolved);
        const selectedServiceType =
          (filters && filters.selectedServiceType) ||
          (resolved ? "" : EventBus.selectedServiceType) ||
          "";
        const selectedSubType =
          (filters && filters.selectedSubType) ||
          (resolved ? "" : EventBus.selectedSubtype) ||
          "";
        const selectedServiceInfo =
          (filters && filters.selectedServiceInfo) ||
          (resolved ? "" : EventBus.selectedServiceInfo) ||
          "";

        // If still empty, infer main category from current route (jobs/sale/services/rentals/events)
        let inferredType = selectedServiceType;
        if (!inferredType) {
          const p = (this.$route && this.$route.path || "").toLowerCase();
          if (p.includes("/jobs")) inferredType = "Jobs";
          else if (p.includes("/sale")) inferredType = "Sale";
          else if (p.includes("/services")) inferredType = "Services";
          else if (p.includes("/rentals")) inferredType = "Rentals";
          else if (p.includes("/events")) inferredType = "Events";
        }

        const payload = {
          userinput: (filters && filters.userinput) || "",
          selectedServiceType: inferredType || selectedServiceType,
          selectedSubType: selectedSubType,
          selectedServiceInfo: selectedServiceInfo,
          selectedDistanceValue: {
            value: (filters && filters.distance) || null,
          },
          page: page,
          perPage: this.perPage,
          sortBy: sortBy,
          preferredCountryName: filters.preferredCountryName || EventBus.selectedCountryName || "",
          // Pass through coords when provided (e.g. from geolocation on first load)
          latOverride: (filters && filters.latOverride) || null,
          lngOverride: (filters && filters.lngOverride) || null,
          // State code for geo-fallback when posts lack geoLocation coordinates
          stateCode: (filters && filters.stateCode) || null,
                    // Photos Only toggle pass-through
          withPhotosOnly: !!(filters && filters.withPhotosOnly),

        };

        // remember applied filters so subsequent identical updates don't reset
        // pagination. Mirror every field that canonicalFilterKey() considers,
        // in the SAME shape as the incoming filters prop — otherwise the next
        // filters-watcher run will see a diff that doesn't really exist and
        // reset the user's current page back to 1.
        this.appliedFilters = {
          userinput: payload.userinput,
          distance: (filters && filters.distance) || null,
          selectedServiceType: payload.selectedServiceType,
          selectedSubType: payload.selectedSubType,
          selectedServiceInfo: payload.selectedServiceInfo,
          withPhotosOnly: payload.withPhotosOnly,
          sortBy: sortBy,
          preferredCountryName: payload.preferredCountryName || "",
          latOverride: (filters && filters.latOverride) || null,
          lngOverride: (filters && filters.lngOverride) || null,
          stateCode: (filters && filters.stateCode) || null,
          // Widening is sticky across pages: page 2 of a widened query is still
          // widened, so the flag has to survive this round-trip. Dropping it
          // here let the EventBus re-narrow page 3.
          _resolved: resolved,
        };

        const { services, allservicesCount, resolvedLat, resolvedLng } =
          await getDashboardData({ ...payload, signal });

        if (!isLatest()) return;

        // this.services = services || [];
        this.services = [...(services || [])];
        // ✅ total count from API
        console.log("total count", allservicesCount);
        console.log(services);
        const totalCount = allservicesCount?.totalCount || this.services.length;

        // ✅ compute total pages
        this.totalResults = totalCount;
        this.totalPages = Math.ceil(totalCount / this.perPage);

        // Record the coords that actually scoped this query (autocomplete may
        // have resolved them from a typed userinput) so sessionStorage can
        // persist them for back-from-detail restore. Without this, a hero
        // search for "New York" persisted latOverride=null and the restore
        // refetched unscoped when autocomplete didn't re-run.
        this.appliedFilters.latOverride =
          resolvedLat || this.appliedFilters.latOverride || null;
        this.appliedFilters.lngOverride =
          resolvedLng || this.appliedFilters.lngOverride || null;

        // sessionStorage now reflects the query that produced the results on
        // screen — so back-from-detail rehydrates exactly that query.
        this.persistState();

        // Nothing matched: widen the query (location first, then service type)
        // rather than showing an empty page — see widenEmptyResults().
        if (totalCount === 0) {
          const widened = await this.widenEmptyResults({ payload, sortBy, signal, isLatest });
          if (!isLatest()) return;
          if (widened) return;
          this.fetchEmptyStateSuggestions();
        } else {
          this.suggestions = null;
        }
      } catch (err) {
        // A superseded request was aborted — the newer fetch owns the UI now.
        if (axios.isCancel?.(err) || err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
          return;
        }
        if (!isLatest()) return;
        console.error("Failed to fetch dashboard data", err);
        this.services = [];
        this.totalResults = 0;
        this.totalPages = 1;
        this.fetchEmptyStateSuggestions();
      } finally {
        if (isLatest()) this.loading = false;
      }
    },
 
    // ── Empty-result widening ───────────────────────────────────────────────
    // A structured search that matches nothing used to leave the user on the
    // "No Listings Found" panel. Mirror what /deals does instead: re-run the
    // query with the scope that killed it removed, and say so.
    //
    // Order matters — the query is widened one level at a time, from the most
    // specific scope to the least, and it STOPS at the user's category:
    //
    //   location  -> same service type, every location
    //   leaf      -> drop the 3rd-level leaf, keep its sub-category
    //   sub-type  -> drop the sub-category, keep its category ("Services")
    //
    // The category itself is never dropped: a dead "Services › Service Wanted
    // in Dallas" widens to every Services listing, NOT to every listing on the
    // site — showing Jobs and Rentals to someone browsing Services is noise,
    // not a fallback. If the category has nothing either, the empty-state panel
    // takes over. Only a search with no recognisable category at all (free text
    // the AI could not map) can still widen to everything.
    //
    // Menu clicks clear the levels above the one selected, so the payload often
    // carries just a sub-type or a leaf — taxonomyParents() recovers the levels
    // above it from servicesType.json. The Photos Only toggle and the country
    // selector are the user's explicit scope and are never dropped.
    //
    // The widened query becomes appliedFilters, so pagination, sorting and
    // back-from-detail all keep working against the results actually on screen.
    //
    // queryLabel / payloadUntried serve the AI branch (see widenAISearch): the
    // payload there is rebuilt from the parsed intent and has never been run,
    // so it is tried as-is first, and the notice names the query rather than
    // the filters.
    async widenEmptyResults({ payload, sortBy, signal, isLatest, queryLabel = "", payloadUntried = false }) {
      const hasLocation = !!(
        payload.userinput ||
        payload.latOverride ||
        payload.lngOverride ||
        payload.stateCode ||
        (payload.selectedDistanceValue && payload.selectedDistanceValue.value)
      );
      const hasType = !!(
        payload.selectedServiceType ||
        payload.selectedSubType ||
        payload.selectedServiceInfo
      );
      // An unscoped payload has nothing left to drop — unless it has never been
      // run, in which case running it IS the widening.
      if (!hasLocation && !hasType && !payloadUntried) return false;

      const locationLabel = this.scopeLocationLabel(payload);
      const typeLabel =
        payload.selectedServiceInfo ||
        payload.selectedSubType ||
        payload.selectedServiceType ||
        "";

      // Levels above whatever the payload actually carries. A leaf resolves to
      // both its sub-type and its category; a sub-type resolves to its category.
      const leafParents = taxonomyParents(payload.selectedServiceInfo);
      const parentSubType = payload.selectedSubType || leafParents.subType || "";
      const category =
        payload.selectedServiceType ||
        taxonomyParents(parentSubType).category ||
        leafParents.category ||
        "";

      const withoutLocation = {
        userinput: "",
        selectedDistanceValue: { value: null },
        latOverride: null,
        lngOverride: null,
        stateCode: null,
      };

      const attempts = [];
      // The rebuilt AI payload keeps whatever category the AI parsed out, with
      // the query text and the geo scope already gone — try that before giving
      // the sub-type up too.
      if (payloadUntried) {
        attempts.push({ scope: "query", relaxed: {} });
      }
      if (hasLocation) {
        attempts.push({ scope: "location", relaxed: withoutLocation });
      }
      // Every rung below has already proved the location is not the problem, so
      // they all drop it along with the level they widen.
      const upFromHere = hasLocation ? withoutLocation : {};
      // A 3rd-level leaf ("Single Family Homes") widens to its sub-category
      // ("Residential Properties") before the sub-category widens to Real Estate.
      if (payload.selectedServiceInfo && parentSubType) {
        attempts.push({
          scope: "leaf",
          widenedTo: parentSubType,
          relaxed: {
            ...upFromHere,
            selectedServiceType: category,
            selectedSubType: parentSubType,
            selectedServiceInfo: "",
          },
        });
      }
      // The floor: everything in the category the user was browsing.
      if (category && (payload.selectedSubType || payload.selectedServiceInfo)) {
        attempts.push({
          scope: "subtype",
          widenedTo: category,
          relaxed: {
            ...upFromHere,
            selectedServiceType: category,
            selectedSubType: "",
            selectedServiceInfo: "",
          },
        });
      }
      // No category to fall back on (free text the taxonomy does not know), so
      // every listing is the only widening left.
      if (hasType && !category) {
        attempts.push({
          scope: hasLocation ? "both" : "type",
          relaxed: {
            ...upFromHere,
            selectedServiceType: "",
            selectedSubType: "",
            selectedServiceInfo: "",
          },
        });
      }

      for (const attempt of attempts) {
        const widenedPayload = { ...payload, ...attempt.relaxed, page: 1 };
        const { services, allservicesCount } = await getDashboardData({
          ...widenedPayload,
          signal,
        });
        if (!isLatest()) return true; // a newer fetch owns the UI — stop here

        const list = [...(services || [])];
        const totalCount = allservicesCount?.totalCount || list.length;
        if (!totalCount) continue; // still nothing — try the next widening

        this.adoptWidenedResults({
          widenedPayload,
          list,
          totalCount,
          sortBy,
          scope: attempt.scope,
          message: this.widenedMessage({
            scope: attempt.scope,
            locationLabel,
            typeLabel,
            queryLabel,
            widenedTo: attempt.widenedTo || "",
          }),
        });
        return true;
      }

      return false; // nothing anywhere — the empty state panel takes over
    },

    // Put a widened query's results on screen and make that query the applied
    // one. Shared by the automatic widening above and the "Show all …" button
    // (showAllInScope) — both replace the current result set with a broader one
    // and have to leave pagination, sorting and back-from-detail pointing at
    // what the user can actually see.
    adoptWidenedResults({ widenedPayload, list, totalCount, sortBy, scope, message }) {
      this.services = list;
      this.totalResults = totalCount;
      this.totalPages = Math.ceil(totalCount / this.perPage);
      this.suggestions = null;

      // The widened query is now the applied query — the currentPage watcher
      // paginates off appliedFilters, so page 2 must not re-apply the dead
      // location/type scope.
      this.appliedFilters = {
        userinput: widenedPayload.userinput,
        distance: (widenedPayload.selectedDistanceValue && widenedPayload.selectedDistanceValue.value) || null,
        selectedServiceType: widenedPayload.selectedServiceType,
        selectedSubType: widenedPayload.selectedSubType,
        selectedServiceInfo: widenedPayload.selectedServiceInfo,
        withPhotosOnly: widenedPayload.withPhotosOnly,
        sortBy: sortBy,
        preferredCountryName: widenedPayload.preferredCountryName || "",
        latOverride: null,
        lngOverride: null,
        stateCode: null,
        // The dropped levels are dropped on purpose — see the _resolved note in
        // fetchDashboardData. Without this, page 2 re-applies them from the
        // EventBus and undoes the widening.
        _resolved: true,
      };

      // Widened results are always page 1. Guard the watcher so it doesn't
      // fire a duplicate fetch for the page we just filled.
      if (this.currentPage !== 1) {
        this.isRestoring = true;
        this.currentPage = 1;
        this.$nextTick(() => { this.isRestoring = false; });
      }

      // Announced by the notice under the filter bar only — a toast saying
      // the same thing at the same moment reads as two separate problems.
      this.listingFallback = { scope, message };

      this.persistState();
    },

    // AI search that matched nothing. /api/ai-search has already retried the
    // text query nationwide (that is what geoFallback means), so the text
    // itself is what found nothing — there is no location left to drop. Fall
    // back to the sub-type the AI parsed out of the query, then to its category,
    // via the same staged widening the structured branch uses. It stops there
    // too: only a query the AI mapped to no category at all reaches every
    // listing.
    async widenAISearch({ query, intent, sortBy, signal, isLatest }) {
      // Leaving sticky AI mode: pagination and back-from-detail have to re-run
      // the widened structured query, not the dead AI one. Cleared BEFORE the
      // widening so the persistState() inside it saves the new truth.
      const stickyQuery = this.activeAISearchQuery;
      const stickyGeo = this.activeAISearchGeo;
      const stickyGeoFallback = this.aiGeoFallback;
      this.activeAISearchQuery = "";
      this.activeAISearchGeo = null;
      // The server's own "showing results from other areas" line would win the
      // v-if over the widening notice, and it is no longer the whole story.
      this.aiGeoFallback = false;

      const widened = await this.widenEmptyResults({
        payload: {
          userinput: "",
          selectedServiceType: (intent && intent.serviceType1) || "",
          selectedSubType: (intent && intent.serviceType2) || "",
          selectedServiceInfo: "",
          selectedDistanceValue: { value: null },
          page: 1,
          perPage: this.perPage,
          sortBy,
          preferredCountryName: EventBus.selectedCountryName || "",
          latOverride: null,
          lngOverride: null,
          stateCode: null,
          withPhotosOnly: this.filters?.withPhotosOnly === true,
        },
        sortBy,
        signal,
        isLatest,
        queryLabel: query,
        payloadUntried: true,
      });

      if (widened) {
        // The mirroring block above only absorbs the raw query when the AI
        // returned an intent. Without one it would still be sitting in the
        // EventBus, and the next page turn would re-enter the AI branch and
        // re-run the dead query (another Claude call) instead of paginating
        // the widened results.
        EventBus.clearSearchQuery();
      } else {
        // Nothing to show either way — put the AI state back so the empty-state
        // panel and the persisted session still describe the search that ran.
        this.activeAISearchQuery = stickyQuery;
        this.activeAISearchGeo = stickyGeo;
        this.aiGeoFallback = stickyGeoFallback;
      }
      return widened;
    },

    // Human label for the location scope that produced no results. userinput is
    // the typed/selected place; a coords-only scope (geolocation, IP guess) has
    // no text of its own, so fall back to the EventBus label and finally to a
    // generic phrase.
    scopeLocationLabel(payload) {
      if (payload && payload.userinput) return payload.userinput;
      const loc = EventBus.selectedLocation;
      if (typeof loc === "string" && loc.trim()) return loc.trim();
      if (loc && typeof loc === "object" && loc.label) return loc.label;
      return "";
    },

    // `widenedTo` is the scope the results actually came from — the sub-category
    // for a dropped leaf, the category for a dropped sub-category. Naming it is
    // the whole point of the notice: "showing all Services listings" tells the
    // user they are still inside Services.
    widenedMessage({ scope, locationLabel = "", typeLabel = "", queryLabel = "", widenedTo = "" } = {}) {
      // AI search: the text is what found nothing, so name it rather than the
      // filters the user never set. Long queries are trimmed — the notice is a
      // one-liner, not a transcript.
      if (queryLabel) {
        const q = queryLabel.length > 60 ? `${queryLabel.slice(0, 60).trim()}…` : queryLabel;
        const kept = scope === "query" ? typeLabel : widenedTo;
        return kept
          ? `Nothing matches “${q}” — showing all ${kept} listings.`
          : `Nothing matches “${q}” — showing all listings.`;
      }
      const where = locationLabel ? `in ${locationLabel}` : "in your area";
      if (scope === "location") {
        return typeLabel
          ? `No ${typeLabel} listings found ${where} — showing ${typeLabel} listings from all locations.`
          : `No listings found ${where} — showing listings from all locations.`;
      }
      // Widened up the taxonomy but still inside the user's category.
      if (scope === "leaf" || scope === "subtype") {
        return `No ${typeLabel || "matching"} listings found ${where} — showing all ${widenedTo} listings.`;
      }
      if (scope === "type") {
        return `No ${typeLabel || "matching"} listings found — showing all listings.`;
      }
      // both
      return `No ${typeLabel || "matching"} listings found ${where} — showing all listings.`;
    },

    postad() {
    if (!this.isvalid) {
    localStorage.setItem("redirectTo", this.$route.fullPath);
    this.modalMode = "login";
    this.showModal = true;
  } else {
    this.postMode = "new";
    this.showPostModal = true;
  }
},
    broadenSearch() {
      EventBus.selectedDistance = 50;
      EventBus.userInteracted = true;
      this.suggestions = null;
    },

    // "Show all <Category> listings" — the manual twin of widenEmptyResults.
    // Drops the location and the sub-type/leaf in one go and keeps the category,
    // so a thin result set opens up to everything in the category the user is
    // already browsing (never to the whole site).
    //
    // It re-queries rather than filtering what is on screen: the current results
    // are one page of a scoped query, not a subset we can widen locally.
    async showAllInScope() {
      const scope = this.showAllScope;
      if (!scope) return;
      const base = this.resultsScope;

      // The list is about to be replaced, so a remembered scroll position no
      // longer points at anything — same reason applySort and the page/filters
      // watchers discard it.
      this.discardListScroll();

      // Same stale-response guard fetchDashboardData uses — this runs outside
      // it, so it has to take ownership of the sequence itself or an in-flight
      // filter change could land on top of the widened results.
      this._fetchSeq = (this._fetchSeq || 0) + 1;
      const seq = this._fetchSeq;
      if (this._fetchAbort) {
        try { this._fetchAbort.abort(); } catch { /* ignore */ }
      }
      const controller = (typeof AbortController !== "undefined") ? new AbortController() : null;
      this._fetchAbort = controller;
      const signal = controller ? controller.signal : null;
      const isLatest = () => seq === this._fetchSeq;

      // Leaving sticky AI mode, if we were in it: page 2 must paginate the
      // widened category query, not re-run the AI query (another Claude call).
      this.activeAISearchQuery = "";
      this.activeAISearchGeo = null;
      this.aiGeoFallback = false;
      EventBus.clearSearchQuery();

      const widenedPayload = {
        userinput: "",
        selectedServiceType: scope.category,
        selectedSubType: "",
        selectedServiceInfo: "",
        selectedDistanceValue: { value: null },
        page: 1,
        perPage: this.perPage,
        sortBy: this.sortBy,
        preferredCountryName: base.preferredCountryName || EventBus.selectedCountryName || "",
        latOverride: null,
        lngOverride: null,
        stateCode: null,
        // The Photos Only toggle is the user's explicit choice, not a scope the
        // button is here to undo.
        withPhotosOnly: base.withPhotosOnly === true,
      };

      this.loading = true;
      this.listingFallback = null;
      try {
        const { services, allservicesCount } = await getDashboardData({
          ...widenedPayload,
          signal,
        });
        if (!isLatest()) return;

        const list = [...(services || [])];
        const totalCount = allservicesCount?.totalCount || list.length;
        // A superset of the current query can only come back empty if something
        // went wrong upstream — leave the results the user already has.
        if (!totalCount) return;

        this.adoptWidenedResults({
          widenedPayload,
          list,
          totalCount,
          sortBy: this.sortBy,
          scope: "show-all",
          message: scope.category
            ? `Showing all ${scope.category} listings.`
            : "Showing all listings.",
        });
      } catch (err) {
        if (axios.isCancel?.(err) || err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
          return;
        }
        if (isLatest()) console.error("Failed to show all listings", err);
      } finally {
        if (isLatest()) this.loading = false;
      }
    },
    searchByCategory(categoryName) {
      EventBus.clearSearchQuery();
      EventBus.setServiceType(categoryName);
      EventBus.selectedSubtype = "";
      EventBus.selectedDistance = 50;
      EventBus.userInteracted = true;
      this.suggestions = null;
    },
    searchByCity(city) {
      EventBus.clearSearchQuery();
      if (city.lat && city.lng) {
        EventBus.setLocation({ lat: city.lat, lng: city.lng, label: city.city });
      } else {
        EventBus.setLocation(city.city);
      }
      // The suggestion's lat/lng is the city centroid (averaged from its
      // listings, see modules/services.getEmptyStateSuggestions). City scopes
      // vary widely — 10 mi is too tight when listings span several
      // townships. Use 30 mi to reliably cover the city the user clicked.
      EventBus.selectedDistance = 30;
      EventBus.userInteracted = true;
      this.suggestions = null;
    },
    navigateToPost(item) {
      // Empty-state suggestion rows carry no serviceID to look the stored slug
      // up with, so this one can only derive — but it must derive with the same
      // rule the link record was created from (see utils/slug.js). The local
      // rule here collapsed every punctuation run to a single dash, which a
      // title like "Room for rent - quiet" stores as "---".
      this.$router.push(`/view-post/${canonicalSlug(item.servicesName)}`);
    },
    onRecentImgError(e) {
      // Broken listing image → fall back to the default placeholder.
      // Guard against an error loop if the placeholder itself ever fails.
      const img = e.target;
      if (img.src !== this.defaultImage) {
        img.src = this.defaultImage;
      }
    },
    async fetchEmptyStateSuggestions() {
      // Try multiple sources for lat/lng
      const loc = EventBus.selectedLocation;
      let lat = (loc && typeof loc === "object") ? loc.lat : null;
      let lng = (loc && typeof loc === "object") ? loc.lng : null;

      // Fallback: try saved user location from localStorage
      if (!lat || !lng) {
        try {
          const saved = JSON.parse(localStorage.getItem("userLocation") || "{}");
          lat = saved.lat || null;
          lng = saved.lng || null;
        } catch { /* ignore */ }
      }

      // Fallback: try IP-based geolocation
      if (!lat || !lng) {
        try {
          const res = await axios.get("/api/geo-location");
          lat = res.data?.lat;
          lng = res.data?.lng;
        } catch { /* ignore */ }
      }

      if (!lat || !lng) return;
      this.suggestions = await getEmptyStateSuggestions({
        lat, lng,
        serviceType1: this.filters?.selectedServiceType || "",
        serviceType2: this.filters?.selectedSubType || "",
        withPhotosOnly: this.filters?.withPhotosOnly === true,
        preferredCountryName:
          this.filters?.preferredCountryName || EventBus.selectedCountryName || "",
      });
    },
  verifyToken() {
    const token = localStorage.getItem("auth-token");
    // Guest (no token): asking the server would 401 with "Invalid or expired
    // token", which the global interceptor treats as an expired session and
    // shows the "session expired" banner to a user who never signed in.
    if (!token) {
      this.isvalid = false;
      return;
    }
    axios.post("/api/verify-token", {}, {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    })
    .then(() => (this.isvalid = true))
    .catch(() => (this.isvalid = false));
  },
}
  };
</script>

<style scoped>
@media (max-width: 480px) {
  /* Single compact toolbar row — everything fits on one line */
  .main-section {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 6px 12px;
    margin: 6px auto 0;
  }

  .result-sec p {
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
  }

  /* Right section: all controls inline */
  .right-section {
    flex-direction: row;
    gap: 6px;
    align-items: center;
    flex-shrink: 0;
  }

  /* View toggle — compact, icon only (hide label text) */
  .view-section {
    border: 1px solid var(--color-border);
    border-radius: 6px;
    overflow: hidden;
    width: auto;
  }

  .list-view,
  .grid-view {
    height: 30px;
    padding: 0 8px;
    gap: 0;
  }

  .list-view p,
  .grid-view p {
    display: none;
  }

  .list-view img,
  .grid-view img {
    width: 14px;
    height: 14px;
  }

  /* Sort + Reset — compact pill group */
  .filter-reset-group {
    border: 1px solid var(--color-border);
    border-radius: 6px;
    overflow: hidden;
  }

  .filter-reset-item {
    padding: 0 8px;
    height: 30px;
    font-size: 12px;
    border-right: 1px solid var(--color-border);
  }

  .filter-reset-item:last-child {
    border-right: none;
  }

  .filter-reset-item span {
    font-size: 12px;
  }
}

.content-wrapper {
  position: relative;
  width: 100%;
  transition: opacity 0.15s ease;
}

.content-wrapper.is-loading {
  opacity: 0.45;
  pointer-events: none;
}

.loader-overlay {
  position: absolute;
  top: 60px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
}

.grid-card-wrapper {
  width: 100%;
  max-width: 1280px;
  margin: auto;
  display: flex;
  justify-content: center;
}

.grid-container {
  width: 100%;
  display: flex;
  justify-content: center;
}

.main-section {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  max-width: 1280px;
  padding: 4px 24px;
  margin: 8px auto 0 auto;
  border-radius: 4px;
  background-color: var(--color-background);
  box-sizing: border-box;
}

.main-section.list-mode {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  width: 100%;
  max-width: 1200px;
  padding: 10px 24px;
  margin: 24px auto 0 auto;
  border-radius: 4px;
  box-sizing: border-box;
  background-color: var(--color-background);
}

.main-section.map-mode {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  width: 100%;
  max-width: 1280px;
  padding: 10px 24px;
  margin: 24px auto 0 auto;
  border-radius: 4px;
  box-sizing: border-box;
  background-color: var(--color-background);
}

.result-sec p {
  font-family: var(--font-primary), sans-serif;
  font-size: 14px;
  line-height: 18px;
  color: var(--color-text-primary);
  margin: 0;
}

.geo-fallback-notice {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px !important;
  padding: 4px 10px;
  border-radius: 8px;
  background: #fef3c7;
  color: #92400e !important;
  font-size: 13px !important;
}

.geo-fallback-notice svg {
  flex-shrink: 0;
}

.right-section {
  display: flex;
  align-items: center;
  gap: 16px;
}

.view-section {
  display: flex;
  align-items: center;
  border: 1px solid var(--color-border);
  border-radius: 4px;
}

.list-view,
.grid-view {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  height: 36px;
  padding: 0 16px;
  background-color: var(--color-bg-primary);
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.list-view img,
.grid-view img {
  width: 14px;
  height: 14px;
}

.list-view p,
.grid-view p {
  font-family: var(--font-primary), sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: var(--color-text-secondary);
  margin: 0;
}

.list-view:hover,
.grid-view:hover {
  background-color: var(--color-bg-hover);
}

.list-view.active,
.grid-view.active {
  background-color: var(--color-primary);
  border-color: var(--color-primary);
  border-radius: 04px;
}

.list-view.active p,
.grid-view.active p {
  color: var(--color-text-inverse);
}

.list-view.active img,
.grid-view.active img {
  filter: brightness(0) invert(1);
}

@media (max-width: 1366px) {
  .main-section {
    width: 95%;
  }
}

@media (max-width: 768px) {
  .main-section {
    width: calc(100% - 32px);
    padding: 10px 16px;
  }

  .list-view,
  .grid-view {
    padding: 0 10px;
    gap: 6px;
  }

  .list-view p,
  .grid-view p {
    font-size: 13px;
    white-space: nowrap;
  }

  .filters-btn span {
    font-size: 13px;
  }
}

@media (min-width: 980px) and (max-width: 1021px) {}

@media (max-width: 480px) {
  .main-section {
    flex-direction: column;
    gap: 12px;
  }

  .top-row,
  .result-sec {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
  }

  .result-sec p {
    font-size: 13px;
    color: #6b7280;
  }

  .result-text {
    text-align: left;
    margin: 0;
  }

  .filter-reset-group {
    display: flex;
    gap: 8px;
  }

  .view-section {
    width: 100%;
    display: flex;
    justify-content: space-between;
    gap: 4px;
  }

  .view-section>div {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 8px 0;
    gap: 4px;
  }

  .list-view p,
  .grid-view p {
    text-align: center;
    margin: 0;
    font-size: 13px;
  }

  .right-section img {
    display: none;
  }

  .filter-reset-item {
    position: relative !important;
    overflow: visible !important;
  }

  .simple-filter-menu {
    position: absolute !important;
    top: 38px !important;
    right: 0 !important;
    z-index: 99999 !important;
  }
}

.filter-reset-group {
  display: flex;
  align-items: center;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  overflow: visible !important;
}

.filter-reset-item {
  position: relative;
  /* important for absolute menu */
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
  height: 36px;
  cursor: pointer;
  border-right: 1px solid var(--color-border);
  z-index: 10;
  /* ensure above other elements */
}

.simple-filter-group {
  position: relative;
  display: flex;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  background: var(--color-background);
}

.simple-filter-btn {
  padding: 8px 16px;
  border-right: 1px solid var(--color-border);
  cursor: pointer;
}

.simple-filter-btn:last-child {
  border-right: none;
}

.simple-filter-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  min-width: 160px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.10);
  padding: 6px 0;
  border-radius: 10px;
  z-index: 9999;
  overflow: visible;
}

/* Invisible bridge to prevent dropdown from closing */
.simple-filter-menu::before {
  content: "";
  position: absolute;
  top: -10px;
  left: 0;
  right: 0;
  height: 10px;
  background: transparent;
}

@keyframes dropdownFadeIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.simple-filter-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 450;
  color: #374151;
  cursor: pointer;
  font-family: "Inter", sans-serif;
  transition: background 0.15s, color 0.15s;
}

.simple-filter-item:first-child {
  border-radius: 10px 10px 0 0;
}

.simple-filter-item:last-child {
  border-radius: 0 0 10px 10px;
}

.simple-filter-item:hover {
  background: #f0f4ff;
  color: var(--color-primary, #3467d5);
}

.simple-filter-item.active-sort {
  background: #f0f4ff;
  color: var(--color-primary, #3467d5);
  font-weight: 600;
}

.filter-reset-item:last-child {
  border-right: none;
}

.filter-reset-item:hover {
  background-color: var(--color-hover);
}

.filter-reset-item img {
  width: 14px;
  height: 14px;
}

.filter-reset-item span {
  font-family: var(--font-primary), sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-secondary);
}

.filter-count {
  color: var(--color-text-secondary);
  font-size: 12px;
  border-radius: 12px;
}

.empty-state-panel {
  width: 100%;
  max-width: 1280px;
  margin: 8px auto 0 auto;
  padding: 12px 24px;
  text-align: center;
}

.empty-state-content {
  background: linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%);
  border: 1px solid #e0e7ff;
  border-radius: 16px;
  padding: 36px 48px;
  max-width: 900px;
  margin: 0 auto;
  box-shadow: 0 8px 24px rgba(63, 93, 192, 0.08);
}

.empty-icon {
  margin-bottom: 20px;
  opacity: 0.6;
}

.empty-title {
  font-size: 28px;
  font-weight: 800;
  color: #1f2345;
  margin: 0 0 12px 0;
}

.empty-description {
  font-size: 16px;
  color: #4b4f68;
  line-height: 1.6;
  margin: 0 0 28px 0;
}

.empty-btn-post {
  display: inline-block;
  padding: 10px 18px;
  background: var(--primary-gradient);
  color: var(--color-text-inverse);
  font-weight: 500;
  font-size: 16px;
  border-radius: 4px;
  text-decoration: none;
  border: none;
  cursor: pointer;
  font-family: "Inter", sans-serif;
  line-height: 24px;
  transition: 0.3s ease;
}

.empty-btn-post:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}
.empty-btns {
  display: inline-block;
  padding: 12px 28px;
  background: #3467D5;
  color: #fff;
  font-weight: 600;
  font-size: 15px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  transition: 0.2s ease;
}
.empty-btns:hover { background: #2851a3; transform: translateY(-1px); }

/* ── Broaden suggestion ── */
.empty-broaden { margin-bottom: 24px; }
.broaden-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  background: #fff;
  border: 1.5px solid #3467D5;
  color: #3467D5;
  font-weight: 600;
  font-size: 14px;
  border-radius: 8px;
  cursor: pointer;
  transition: 0.2s;
}
.broaden-btn:hover { background: #3467D5; color: #fff; }
.broaden-btn svg { flex-shrink: 0; }

/* ── "Show all <Category> listings" under a thin result set ── */
.show-all-bar {
  display: flex;
  justify-content: center;
  padding: 8px 16px 24px;
}
.show-all-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 22px;
  background: #fff;
  border: 1.5px solid #3467D5;
  color: #3467D5;
  font-weight: 600;
  font-size: 14px;
  border-radius: 8px;
  cursor: pointer;
  transition: 0.2s;
}
.show-all-btn:hover { background: #3467D5; color: #fff; }
/* An icon-only flex child collapses to zero width without this. */
.show-all-btn svg { flex: 0 0 16px; }

/* ── Section title ── */
.empty-section-title {
  font-size: 14px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 12px 0;
}

/* ── Category chips ── */
.empty-categories { margin-bottom: 24px; }
.category-chips { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; }
.category-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  cursor: pointer;
  transition: 0.2s;
}
.category-chip:hover { border-color: #3467D5; color: #3467D5; background: #f0f4ff; }
.chip-count {
  background: #e5e7eb;
  color: #6b7280;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
}
.category-chip:hover .chip-count { background: #dbeafe; color: #3467D5; }

/* ── Nearby cities ── */
.empty-cities { margin-bottom: 24px; }
.city-cards { display: flex; flex-wrap: wrap; gap: 10px; justify-content: center; }
.city-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 20px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  transition: 0.2s;
  min-width: 130px;
}
.city-card:hover { border-color: #3467D5; background: #f0f4ff; box-shadow: 0 2px 8px rgba(52, 103, 213, 0.1); }
.city-name { font-size: 15px; font-weight: 600; color: #1f2937; }
.city-meta { font-size: 12px; color: #9ca3af; margin-top: 4px; }

/* ── Recent nearby listings ── */
.empty-recent { margin-bottom: 28px; }
.recent-cards { display: flex; flex-wrap: wrap; gap: 12px; justify-content: center; }
.recent-card {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 10px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  transition: 0.2s;
  width: 280px;
  text-align: left;
}
.recent-card:hover { border-color: #3467D5; box-shadow: 0 2px 8px rgba(52, 103, 213, 0.1); }
.recent-card-img {
  width: 64px;
  height: 64px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
  background: #f3f4f6;
}
.recent-card-info { flex: 1; min-width: 0; }
.recent-card-title {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 2px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.recent-card-location {
  font-size: 12px;
  color: #9ca3af;
  margin: 0 0 4px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.recent-card-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  color: #3467D5;
  background: #eff3ff;
  padding: 2px 8px;
  border-radius: 4px;
}
.recent-card-dist {
  font-size: 11px;
  color: #9ca3af;
  margin-left: 6px;
}

/* ── CTA ── */
.empty-cta { margin-top: 8px; }
.empty-cta-text {
  font-size: 15px;
  font-weight: 500;
  color: #4b4f68;
  margin: 0 0 12px 0;
}

/* ── Mobile responsive ── */
@media (max-width: 640px) {
  .empty-state-content { padding: 24px 16px; }
  .empty-title { font-size: 22px; }
  .recent-card { width: 100%; }
  .city-card { min-width: 110px; padding: 10px 14px; }
}
</style>
